import React, { useMemo, useState } from 'react';
import { createStyles } from 'utils/createStyle';
import { ECommBrand } from 'common/api/e-comm/models/ECommBrand';
import { useSpring, animated, SpringValue } from 'react-spring';
import useMediaQueries from 'hooks/useMediaQueries';

interface BrandLogoProps {
    brand?: ECommBrand;
    renderers?: {
        logo: React.ElementType;
    };
}

const DefaultLogoRenderer = ({ brand }: Pick<BrandLogoProps, 'brand'>) => {
    return brand ? <img src={brand.imageUrl} /> : null;
};

export const BrandLogo = ({ brand, renderers = { logo: DefaultLogoRenderer } }: BrandLogoProps) => {
    const [logoLoadAnimation, styles] = useStyles({ brand: brand });
    return (
        <div className={styles.logoContainer}>
            <div className={styles.logoImage}>
                <animated.div
                    style={{ ...logoLoadAnimation }}
                    className="absolute inset-0 text-center flex items-center align-center justify-center"
                >
                    <renderers.logo brand={brand} />
                </animated.div>
            </div>
        </div>
    );
};

const useStyles = ({
    brand,
}: {
    brand?: ECommBrand;
}): [{ opacity: SpringValue<number> }, { logoContainer: string; logoImage: string }] => {
    const logoStyles = useSpring({
        opacity: brand?.imageUrl ? 1 : 0,
        delay: 500,
    });
    const [dimension, setDimensionTo] = useState<string>('140px');
    const query = useMemo(
        () => ({
            '(max-width: 648px)': () => {
                setDimensionTo('80px');
            },
            '(min-width: 649px)': () => {
                setDimensionTo('140px');
            },
        }),
        []
    );

    useMediaQueries(query);

    const styles = useMemo(() => {
        return createStyles({
            logoContainer: [
                'inline-block',
                {
                    width: dimension,
                    height: dimension,
                },
            ],
            logoImage: [
                'relative border w-full rounded border-grey-3',
                {
                    paddingTop: '100%',
                },
            ],
        });
    }, [dimension]);

    return [logoStyles, styles];
};
